import colors from "../../base/colors";
import pxToRem from "../../functions/pxToRem";
import rgba from "../../functions/rgba";

const { inputBorderColor, primary, error, text, grey } = colors;

const pickerFieldDefaults = {
  size: "small" as const,
  fullWidth: true,
  variant: "outlined" as const,
  InputLabelProps: { shrink: true },
};

const datePickers = {
  MuiPickersTextField: {
    defaultProps: pickerFieldDefaults,
    styleOverrides: {
      root: {
        backgroundColor: "transparent",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        maxWidth: "100%",

        "& .MuiInputLabel-root": {
          position: "relative",
          transform: "none !important",
          maxWidth: "100%",
        },

        "& .MuiPickersOutlinedInput-notchedOutline legend": {
          display: "none",
        },

        "& .MuiPickersOutlinedInput-root": {
          marginTop: 0,
        },

        "& .MuiFormHelperText-root": {
          marginLeft: 0,
          marginRight: 0,
          marginTop: pxToRem(4),
          lineHeight: 1.35,
          overflowWrap: "anywhere",
          fontSize: pxToRem(12),
        },
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: "rgba(255, 255, 255, 0.06)",
        fontSize: pxToRem(14),
        borderRadius: pxToRem(4),
        minHeight: pxToRem(32),
        padding: `0 ${pxToRem(10)}`,
        letterSpacing: "normal",

        "&:hover .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: rgba(inputBorderColor, 0.95),
        },

        "&.Mui-focused": {
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: primary.main,
            borderWidth: "2px",
          },
        },

        "&.Mui-error": {
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: error.main,
          },
        },

        "&.Mui-disabled": {
          backgroundColor: "rgba(255, 255, 255, 0.04)",
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: rgba(inputBorderColor, 0.35),
          },
        },

        "& .MuiIconButton-root": {
          padding: pxToRem(4),
          marginRight: pxToRem(-2),
        },

        "& .MuiSvgIcon-root": {
          fontSize: pxToRem(18),
        },
      },
      notchedOutline: {
        top: 0,
        padding: 0,
        borderColor: "rgba(255, 255, 255, 0.28)",
        borderWidth: "1px",
        transition: "border-color 0.12s ease",
        "& legend": {
          display: "none",
        },
      },
      input: {
        color: text.main,
        padding: `${pxToRem(6)} 0`,
        fontSize: pxToRem(14),
        lineHeight: "20px",
        width: "100%",
        minWidth: 0,
      },
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: {
        fontSize: pxToRem(14),
        letterSpacing: "normal",
      },
      sectionsContainer: {
        width: "100%",
        minWidth: 0,
        padding: `${pxToRem(6)} 0`,
        fontSize: pxToRem(14),
        lineHeight: "20px",
      },
      sectionContent: {
        fontSize: pxToRem(14),
        lineHeight: "20px",
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        backgroundColor: "transparent",
        color: text.main,
        "&.Mui-selected": {
          backgroundColor: `${primary.main} !important`,
          color: `${primary.contrastText} !important`,
          "&:hover, &:focus": {
            backgroundColor: `${primary.focus} !important`,
          },
        },
        "&.MuiPickersDay-today:not(.Mui-selected)": {
          borderColor: primary.main,
        },
      },
    },
  },
  MuiPickerDay2: {
    styleOverrides: {
      root: {
        backgroundColor: "transparent",
        color: text.main,
        "&.Mui-selected": {
          backgroundColor: `${primary.main} !important`,
          color: `${primary.contrastText} !important`,
          "&:hover, &:focus": {
            backgroundColor: `${primary.focus} !important`,
          },
        },
      },
    },
  },
  MuiDigitalClock: {
    styleOverrides: {
      item: {
        minWidth: "0 !important",
        "&.Mui-selected": {
          backgroundColor: `${primary.main} !important`,
          color: `${primary.contrastText} !important`,
          "&:hover, &:focus, &.Mui-focusVisible": {
            backgroundColor: `${primary.focus} !important`,
          },
        },
      },
    },
  },
  MuiMultiSectionDigitalClockSection: {
    styleOverrides: {
      item: {
        minWidth: "0 !important",
        width: "auto",
        maxWidth: "100%",
        margin: `${pxToRem(2)} ${pxToRem(4)}`,
        padding: pxToRem(8),
        justifyContent: "center",
        color: text.main,
        "&.Mui-selected": {
          backgroundColor: `${primary.main} !important`,
          color: `${primary.contrastText} !important`,
          "&:hover, &:focus, &.Mui-focusVisible": {
            backgroundColor: `${primary.focus} !important`,
          },
        },
      },
    },
  },
  MuiDateTimePicker: {
    defaultProps: {
      ampm: false,
      formatDensity: "dense",
      views: ["year", "month", "day", "hours", "minutes"],
      slotProps: {
        textField: pickerFieldDefaults,
        openPickerButton: { size: "small" },
      },
    },
  },
  MuiDatePicker: {
    defaultProps: {
      formatDensity: "dense",
      slotProps: {
        textField: pickerFieldDefaults,
        openPickerButton: { size: "small" },
      },
    },
  },
  MuiTimePicker: {
    defaultProps: {
      ampm: false,
      formatDensity: "dense",
      slotProps: {
        textField: pickerFieldDefaults,
        openPickerButton: { size: "small" },
      },
    },
  },
};

export default datePickers;
